import React from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { extractApiErrorMessage } from "../utils/classGroupApi";

// The backend refuses to publish when question marks don't add up to the test's score, and
// returns { message, suggestion, point_distribution: { [questionId]: marksItWouldGet } }.
// Returns that detail, or null for any other error.
export const parsePublishPointsError = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object" || !data.point_distribution) return null;
  return {
    message: data.message,
    suggestion: data.suggestion,
    pointDistribution: data.point_distribution,
  };
};

export const getPublishErrorMessage = (error) =>
  extractApiErrorMessage(error, "Failed to update test status");

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatMarks = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  if (Number.isNaN(number)) return null;
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

/**
 * Explains why a test can't be published because of its question marks, question by question.
 * `questions` (optional) are the page's questions with { questionId, question, point }; without
 * them the questions are listed by number only.
 */
const PublishTestErrorModal = ({
  details,
  questions = [],
  test,
  onClose,
  onEditQuestions,
  editQuestionsLabel = "Edit questions",
}) => {
  if (!details) return null;

  const isPartialMcq = test?.scoring_mode === "partial_mcq" && test?.mcq_max_score;
  const scoreLabel = isPartialMcq ? "MCQ score" : "total score";
  const targetScore = isPartialMcq ? test.mcq_max_score : test?.total_score;

  const byId = new Map(questions.map((q) => [String(q.questionId || q._id || q.id), q]));
  const rows = Object.entries(details.pointDistribution || {}).map(([id, wouldGet], index) => {
    const question = byId.get(String(id));
    const setMarks = formatMarks(question?.point);
    return {
      id,
      number: index + 1,
      text: question ? stripHtml(question.question) : "",
      setMarks,
      wouldGet: formatMarks(wouldGet) ?? "0",
      changed: setMarks !== null && Math.abs(Number(question.point) - Number(wouldGet)) > 0.01,
    };
  });
  const hasQuestionDetails = rows.some((row) => row.text || row.setMarks !== null);
  const totalSet = rows.reduce((sum, row) => sum + (row.setMarks !== null ? Number(row.setMarks) : 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600 flex-shrink-0">
              <FiAlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                This test can't be published yet
              </h3>
              <p className="text-sm text-slate-600 mt-0.5">
                The marks on its questions don't add up to the test's {scoreLabel}
                {targetScore !== undefined && targetScore !== null ? ` (${targetScore})` : ""}.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
            <p className="text-sm text-amber-900">{details.message}</p>
            {details.suggestion && (
              <p className="text-sm font-semibold text-amber-900">
                What to do: {details.suggestion}
              </p>
            )}
          </div>

          {rows.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold w-12">#</th>
                    {hasQuestionDetails && (
                      <>
                        <th className="px-3 py-2 text-left font-semibold">Question</th>
                        <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                          Marks you set
                        </th>
                      </>
                    )}
                    <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      Marks it would get
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.id} className={row.changed ? "bg-amber-50/50" : ""}>
                      <td className="px-3 py-2 text-slate-500">{row.number}</td>
                      {hasQuestionDetails && (
                        <>
                          <td className="px-3 py-2 text-slate-800">
                            <span className="line-clamp-1">{row.text || `Question ${row.number}`}</span>
                          </td>
                          <td className="px-3 py-2 text-right text-slate-800">
                            {row.setMarks ?? <span className="text-slate-400">Not set</span>}
                          </td>
                        </>
                      )}
                      <td
                        className={`px-3 py-2 text-right font-medium ${
                          row.changed ? "text-amber-700" : "text-slate-800"
                        }`}
                      >
                        {row.wouldGet}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {hasQuestionDetails && (
                  <tfoot className="bg-slate-50 text-xs font-semibold text-slate-700">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>
                        Total
                      </td>
                      <td className="px-3 py-2 text-right">{formatMarks(totalSet)}</td>
                      <td className="px-3 py-2 text-right">{targetScore ?? "—"}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          <p className="text-xs text-slate-500">
            Fix it by changing the marks on the questions, or by editing the test's {scoreLabel}.
            Questions without marks share whatever is left of the {scoreLabel}.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Close
          </button>
          {onEditQuestions && (
            <button
              onClick={onEditQuestions}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              {editQuestionsLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishTestErrorModal;
